/**
 * MembersCheatGuide — how-to for the Members table tab.
 */
import React from 'react';
import { Search, Filter, Send, Download, Edit, RefreshCw, Lock } from 'lucide-react';
import CheatGuideShell, {
  Section, Btn, Pill, Warn, Note, Choice,
} from './CheatGuideShell';

export default function MembersCheatGuide() {
  return (
    <CheatGuideShell
      title="Cheat Guide — finding and acting on members"
      storageKey="sb_members_cheat_open"
      testId="members-cheat-guide">

      <Section title="What lives here?" icon={<Search size={13} />}>
        Every member synced from Zoho CRM for this client.{' '}
        <strong>Source of truth = Zoho CRM.</strong> ScaleBoard mirrors it nightly
        (and on-demand via Refresh). Add-ons (cards/access) sync from a separate
        Zoho module — toggle "Include add-ons" to see them inline.
      </Section>

      <Section title="Finding a member fast" icon={<Filter size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li><Btn icon={<Search size={11} />} label="Search" /> — name, email, phone, membership #</li>
          <li>City filter dropdown — picks from actual city values in your CRM</li>
          <li>"Only with email" / "Include inactive" toggles</li>
          <li>Sort by any column header (DOB sorts by month-day, not year — birthday cohorts)</li>
        </ul>
      </Section>

      <Section title="Bulk Soul Form invites" icon={<Send size={13} />}>
        <Btn icon={<Send size={11} />} label="Bulk invite" /> button at the top.
        Sends a Soul Form invite to every member who hasn't completed one yet.
        Throttled per day to protect deliverability.
        <Warn>
          Don't run this multiple times in the same day — it skips already-invited
          members but still consumes a Resend API call per check.
        </Warn>
      </Section>

      <Section title="Editing a member" icon={<Edit size={13} />}>
        Click any row → drawer opens with the full member record.
        <Choice label="Editable fields" recommended>
          tier, city, DOB, anniversary, dietary, hotel preference, +12 more.
          Saves directly to Zoho CRM via OAuth.
        </Choice>
        <Choice label="Read-only">
          name, email, phone (these come from the master record;
          to change them, edit in Zoho CRM and hit Refresh here).
        </Choice>
      </Section>

      <Section title="Export to CSV" icon={<Download size={13} />}>
        <Btn icon={<Download size={11} />} label="Export" /> downloads the current
        filtered/searched view as CSV.{' '}
        <strong>Respects your filters</strong> — useful for "all Mumbai Platinum
        members" or "members with no Soul Form yet".
      </Section>

      <Section title="Refresh from Zoho" icon={<RefreshCw size={13} />}>
        <Btn icon={<RefreshCw size={11} />} label="Refresh" /> top right.
        Pulls the latest from Zoho CRM. Use this after you've made changes in
        Zoho directly (or after a teammate has).
      </Section>

      <Section title="Permissions" icon={<Lock size={13} />}>
        <Pill bg="#DBEAFE" fg="#1E3A8A">CEO</Pill> +{' '}
        <Pill bg="#DCFCE7" fg="#166534">Leaders</Pill> can edit + bulk-invite.
        Sub-leaders see the table but Edit / Bulk-invite are hidden.
      </Section>

      <Note color="#FFFFFFAA">
        <strong style={{ color: '#7A5C2B' }}>Pro tip:</strong> If a member's
        membership # looks like <code>3.00117E+13</code>, that's a Excel
        scientific-notation glitch from the original CSV import. Tell Dipali — she
        has an admin healer that can fix all of them in one shot.
      </Note>
    </CheatGuideShell>
  );
}
